import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getDatabase, provideDatabase } from '@angular/fire/database';
import { browserLocalPersistence, browserPopupRedirectResolver, browserSessionPersistence, initializeAuth, provideAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { enableProdMode, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { HttpLoaderFactory, appInitializerFactory } from './app/app.config';
import { environment } from './environments/environment';
import { AppComponent } from './app/app.component';
import { ToastrModule } from 'ngx-toastr';
import { provideRouter } from '@angular/router';
import { bootstrapApplication } from '@angular/platform-browser';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { TranslateService, TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { LocalStorageService } from '@services/local-storage/local-storage.service';
import { provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';
import routes from './app/routes/global.routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import { errorInterceptor } from './app/interceptors/error.interceptor';
import { headerInterceptor } from './app/interceptors/header.interceptor';
import { LogLevel, setLogLevel } from '@angular/fire';
import { provideAppCheck, initializeAppCheck, ReCaptchaEnterpriseProvider } from '@angular/fire/app-check';

if (environment.production || window.location.hostname.includes('sandbox.admin.airwallet.net') || window.location.hostname.includes('demo.airwallet.net')) {
    enableProdMode();
    if (!window.location.hostname.includes('localhost') && !window.location.hostname.includes('sandbox.admin.airwallet.net')) {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        window.console.log = function () {};
    }
}

if (!environment.production) {
    (window as any).FIREBASE_APPCHECK_DEBUG_TOKEN = (environment as any).firebaseAppCheckDebugToken;
}

bootstrapApplication(AppComponent, {
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        provideAppCheck(() =>
            initializeAppCheck(getApp(), {
                provider: new ReCaptchaEnterpriseProvider(environment.recaptchaSiteKey),
                isTokenAutoRefreshEnabled: true
            })
        ),
        provideDatabase(() => {
            getDatabase();
            setLogLevel(LogLevel.SILENT); // For now set to silent, since there is an angular - firebase conflict with the console logs.
            return getDatabase();
        }),
        provideAuth(() =>
            initializeAuth(getApp(), {
                persistence: [browserLocalPersistence, browserSessionPersistence],
                popupRedirectResolver: browserPopupRedirectResolver
            })
        ),
        provideFirestore(() => getFirestore()),
        importProvidersFrom(
            ToastrModule.forRoot({
                preventDuplicates: true,
                //Global setting to give all toast 7 seconds
                timeOut: 7000,
                //Make toast go away after 1 second when not hovering over it
                extendedTimeOut: 1000
            }),
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: HttpLoaderFactory,
                    deps: [HttpClient]
                }
            })
        ),
        provideLottieOptions({
            player: () => player
        }),
        provideAnimations(),
        HttpClient,
        provideAppInitializer(() => {
            const initializerFn = appInitializerFactory(inject(TranslateService), inject(LocalStorageService));
            return initializerFn();
        }),
        { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } },
        provideHttpClient(withInterceptors([headerInterceptor, errorInterceptor])),
        provideRouter(routes)
    ]
}).catch(err => console.error(err));
